/** Join funnels stay marketing even for logged-in seekers mid-flow. */
function isJobsJoin(pathname: string) {
  return pathname === "/jobs/join" || pathname.startsWith("/jobs/join/")
}

function isSeekerSharedPath(pathname: string) {
  if (isJobsJoin(pathname)) return false
  return (
    pathname.startsWith("/jobs") ||
    pathname.startsWith("/companies") ||
    pathname.startsWith("/hackathons")
  )
}

export type ChromeRole = "employer" | "seeker"

/** Survives hard refresh so chrome does not flash the wrong shell. */
export const CHROME_ROLE_KEY = "hsr_chrome_role"

export function readCachedChromeRole(): ChromeRole | null {
  if (typeof sessionStorage === "undefined") return null
  const role = sessionStorage.getItem(CHROME_ROLE_KEY)
  return role === "employer" || role === "seeker" ? role : null
}

export function writeCachedChromeRole(role: ChromeRole | null) {
  if (typeof sessionStorage === "undefined") return
  if (!role) {
    sessionStorage.removeItem(CHROME_ROLE_KEY)
    return
  }
  sessionStorage.setItem(CHROME_ROLE_KEY, role)
}

/**
 * Ink & lime CandidateHeader for the seeker desk and the public listing/
 * detail URLs they open from Discover (roles, companies, hackathons).
 */
export function usesCandidateChrome(
  pathname: string,
  opts?: { role?: ChromeRole | null },
): boolean {
  // Desk routes own this chrome — do not wait on profile (avoids SiteHeader flash).
  if (pathname.startsWith("/candidate")) return true
  if (opts?.role !== "seeker") return false
  return isSeekerSharedPath(pathname)
}

/**
 * Ink & lime EmployerHeader for the company desk (/recruit/* except join)
 * and legacy employer aliases.
 */
export function usesEmployerChrome(
  pathname: string,
  opts?: { role?: ChromeRole | null },
): boolean {
  if (pathname.startsWith("/recruit/join")) return false
  if (pathname.startsWith("/employer") || pathname.startsWith("/company")) {
    return true
  }
  if (pathname === "/recruit" || pathname === "/recruit/") {
    return opts?.role === "employer"
  }
  if (pathname.startsWith("/recruit")) return true
  return false
}

/** Ink & lime AdminHeader for the platform operator desk (/admin/*). */
export function usesAdminChrome(pathname: string): boolean {
  return pathname !== "/admin/login" && pathname.startsWith("/admin")
}

/** True when ink & lime desk chrome (candidate or employer) owns the shell. */
export function usesInkDeskChrome(
  pathname: string,
  opts?: { role?: ChromeRole | null },
): boolean {
  return (
    usesCandidateChrome(pathname, opts) || usesEmployerChrome(pathname, opts)
  )
}

/** App desks: ink headers or forest SiteHeader (legacy); marketing elsewhere. */
export function usesAppChrome(
  pathname: string,
  opts?: { role?: ChromeRole | null },
): boolean {
  if (usesInkDeskChrome(pathname, opts)) return true
  if (usesAdminChrome(pathname)) return true
  if (
    pathname.startsWith("/candidate") ||
    pathname.startsWith("/company") ||
    pathname.startsWith("/seeker") ||
    pathname.startsWith("/employer")
  ) {
    return true
  }
  if (pathname.startsWith("/recruit/join")) return false
  if (pathname === "/recruit" || pathname === "/recruit/") {
    return opts?.role === "employer"
  }
  if (pathname.startsWith("/recruit")) return true
  return false
}

export function usesMarketingChrome(
  pathname: string,
  opts?: { role?: ChromeRole | null },
): boolean {
  // The admin credential screen owns its own minimal shell. In particular,
  // it must not surface the public Join action beside privileged sign-in.
  if (pathname === "/admin/login") return false
  return !usesAppChrome(pathname, opts)
}

/**
 * Paths where seeker vs guest (or employer vs guest) picks a different shell.
 * Only these need to wait on auth/profile when role is unknown.
 */
export function chromeDependsOnRole(pathname: string): boolean {
  if (pathname === "/recruit" || pathname === "/recruit/") return true
  return isSeekerSharedPath(pathname)
}

export type MarketingNavKey =
  | "candidates"
  | "companies"
  | "browse"
  | "hackathons"

export function activeMarketingNav(pathname: string): MarketingNavKey | null {
  if (pathname.startsWith("/recruit")) return "companies"
  if (pathname.startsWith("/hackathons")) return "hackathons"
  if (pathname === "/jobs/browse" || pathname.startsWith("/jobs/browse/")) {
    return "browse"
  }
  // Exact candidate landing + join stay under "For candidates"
  if (pathname.startsWith("/jobs")) return "candidates"
  return null
}
